import { Button } from '@tempo-io/sui-button';
import { Calendar, type DateRange } from '@tempo-io/sui-calendar';
import { cn } from '@tempo-io/sui-class-utils';
import { CalendarToday } from '@tempo-io/sui-icons';
import { Popover, PopoverContent, PopoverTrigger } from '@tempo-io/sui-popover';
import { format } from 'date-fns/format';
import { useState } from 'react';
import { parseCalendarDate } from './DateFieldInput';

export const DateRangeFieldInput = ({
  values,
  onChange,
  autoFocus,
  isEditableText,
}: {
  values: string[];
  onChange: (value: DateRange | undefined) => void;
  isEditableText?: boolean;
  autoFocus?: boolean;
}) => {
  const startDate = values[0] ? parseCalendarDate(values[0]) : undefined;
  const endDate = values[1] ? parseCalendarDate(values[1]) : undefined;

  const [open, setOpen] = useState(autoFocus ?? false);
  const [isFirstClick, setIsFirstClick] = useState(true);
  const [calendarSelected, setCalendarSelected] = useState<DateRange | undefined>(() =>
    autoFocus ? { from: startDate, to: endDate } : undefined,
  );

  const displayValue =
    startDate && endDate
      ? `${format(startDate, 'MM/dd/yyyy')} – ${format(endDate, 'MM/dd/yyyy')}`
      : startDate
        ? format(startDate, 'MM/dd/yyyy')
        : '';

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      setCalendarSelected({ from: startDate, to: endDate });
      setIsFirstClick(true);
    } else {
      setCalendarSelected(undefined);
      setIsFirstClick(true);
    }
  };

  const handleSelect = (range: DateRange | undefined) => {
    if (isFirstClick) {
      const clickedDate =
        range?.to && endDate && range.to.getTime() !== endDate.getTime() ? range.to : range?.from;
      if (clickedDate) {
        if (startDate && !endDate && range?.from && range?.to) {
          onChange(range);
          setOpen(false);
          setCalendarSelected(undefined);
          setIsFirstClick(true);
        } else {
          setIsFirstClick(false);
          setCalendarSelected({ from: clickedDate, to: undefined });
        }
      }
    } else if (range?.from && range?.to) {
      onChange(range);
      setOpen(false);
      setCalendarSelected(undefined);
      setIsFirstClick(true);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant={isEditableText ? 'ghost' : 'outline'}
          className={cn(
            'fs-exclude w-full justify-start font-normal',
            isEditableText &&
              cn(
                'group border border-transparent hover:border-border-neutral hover:shadow-xs',
                open && 'border-border-focus-neutral ring-[3px] ring-border-focus-neutral/40',
              ),
          )}
          data-testid="fim3-custom-field-input-date-range"
          data-dd-action-name="FIM 3 Custom Field - date picker redacted"
        >
          <CalendarToday />
          {displayValue || (
            <span className={isEditableText ? 'invisible group-hover:visible' : undefined}>
              Select date range
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto">
        <Calendar
          mode="range"
          selected={calendarSelected}
          defaultMonth={startDate}
          onSelect={handleSelect}
          numberOfMonths={2}
        />
      </PopoverContent>
    </Popover>
  );
};
